import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import AchievementBadges from '../components/AchievementBadges';
import BadgeCollection from '../features/badges/BadgeCollection';
import { getOwnProgress } from '../features/progress/progressApi';
import type { CourseProgress } from '../features/progress/progressApi';

function StudentProgress() {
  const { courseId = '' } = useParams();
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let ignore = false;

    async function loadProgress() {
      try {
        const result = await getOwnProgress(courseId);
        if (!ignore) {
          setProgress(result);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error ? error.message : 'Impossible de charger votre progression.',
          );
        }
      }
    }

    void loadProgress();
    return () => {
      ignore = true;
    };
  }, [courseId, refreshToken]);

  return (
    <main
      style={{
        maxWidth: 720,
        margin: '3rem auto',
        padding: '0 1rem',
        display: 'grid',
        gap: '1.5rem',
      }}
    >
      <header>
        <Link to={`/courses/${courseId}`}>Retour au cours</Link>
        <h1>Ma progression</h1>
      </header>

      {errorMessage && <p role="alert">{errorMessage}</p>}

      {progress && (
        <>
          <section aria-labelledby="total-points-heading">
            <h2 id="total-points-heading">Points totaux</h2>
            <p>{progress.totalPoints} pts</p>
          </section>

          <section aria-labelledby="chapter-completion-heading">
            <h2 id="chapter-completion-heading">Progression par chapitre</h2>
            {progress.completionPctByChapter.length === 0 ? (
              <p>Aucun chapitre pour ce cours.</p>
            ) : (
              <ul>
                {progress.completionPctByChapter.map((chapter) => (
                  <li key={chapter.chapterId}>
                    {chapter.chapterTitle} — {chapter.completionPct}%
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="achievements-heading">
            <h2 id="achievements-heading">Badges</h2>
            <AchievementBadges achievements={progress.achievements} />
          </section>

          {progress.collectibleBadges ? (
            <BadgeCollection
              courseId={courseId}
              badgeCollectionConsented={Boolean(progress.badgeCollectionConsented)}
              badges={progress.collectibleBadges}
              selectedBadgeCode={progress.selectedBadgeCode ?? null}
              onProgressRefresh={() => setRefreshToken((current) => current + 1)}
            />
          ) : null}
        </>
      )}
    </main>
  );
}

export default StudentProgress;
